// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

import { translateRichText, type RichTextNode, type Translator } from "@unilarva/translator";

import type { SiteConfig } from "./types";
import { htmlEncode } from "./generic/object-utils";

export interface RichTranslationTagSpec {
  element: "a" | "em" | "h2" | "h3" | "i" | "img" | "p" | "span" | "strong";
  className?: string;
  /** Marks an SSR placeholder that the browser must replace even when the language is unchanged. */
  clientOnly?: boolean;
  href?: string;
  external?: boolean;
  src?: string;
  width?: number;
  height?: number;
  loading?: "lazy";
  content: "children" | "alt" | "none";
}

function contactEmail(site: SiteConfig): string | null {
  const local = (site.ceh ?? "").trim();
  const domain = (site.cet ?? "").trim();
  if (!local || !domain) return null;
  const email = `${local}@${domain}`;
  return /^[^\s@]+@[^\s@]+$/.test(email) ? email : null;
}

/** Creates the parent application's complete semantic rich-token policy. */
export function createRichTranslationTagSpecs(
  site: SiteConfig,
  exposeContactEmail: boolean,
): Readonly<Record<string, RichTranslationTagSpec>> {
  const email = exposeContactEmail ? contactEmail(site) : null;
  const contactSubject = site.siteId === "book-astral" ? "Unilaiva no Astral" : "Unilaiva Songbook";
  const safeSiteId = /^[a-z0-9-]+$/.test(site.siteId) ? site.siteId : "";

  return {
    paragraph: { element: "p", content: "children" },
    strong: { element: "strong", content: "children" },
    emphasis: { element: "em", content: "children" },
    heading: { element: "h2", content: "children" },
    subheading: { element: "h3", content: "children" },
    contact: { element: "p", className: "sb-home-contact-info", content: "children" },
    "contact-details": { element: "span", content: "children" },
    "info-icon": { element: "i", className: "sbi-info", content: "none" },
    github: {
      element: "a",
      href: "https://github.com/unilaiva",
      external: true,
      content: "children",
    },
    ulsbs: {
      element: "a",
      href: "https://github.com/unilaiva/ulsbs",
      external: true,
      content: "children",
    },
    "pdfjs-viewer": {
      element: "a",
      href: "https://github.com/unilarva/pdfjs-viewer",
      external: true,
      content: "children",
    },
    "unilaiva-songbook": {
      element: "a",
      href: "https://unilaiva.aavalla.net/",
      external: true,
      content: "children",
    },
    "print-guide": {
      element: "a",
      href: "https://github.com/unilaiva/unilaiva-songbook#printing-double-sided-on-a-single-sided-printer",
      external: true,
      content: "children",
    },
    "contact-email": email
      ? {
          element: "a",
          href: `mailto:${email}?subject=${encodeURIComponent(contactSubject)}`,
          content: "children",
        }
      : { element: "span", clientOnly: !exposeContactEmail, content: "none" },
    "author-avatar": {
      element: "img",
      src: safeSiteId ? `/sites/${safeSiteId}/img/larva-avatar.png` : undefined,
      width: 128,
      height: 128,
      loading: "lazy",
      content: "alt",
    },
    "binding-photo": {
      element: "img",
      className: "sb-feather",
      src: "/sites/book-astral/img/photo_spread_in_compilacao_completa_v2023-12-01_with_32mm_wire_binding.jpg",
      width: 1920,
      height: 1087,
      loading: "lazy",
      content: "alt",
    },
  };
}

function textContent(nodes: readonly RichTextNode[]): string {
  return nodes
    .map(node => (node.type === "text" ? node.value : textContent(node.children)))
    .join("");
}

function serializeAttributes(spec: RichTranslationTagSpec, alt: string | null): string {
  const attributes: string[] = [];
  if (spec.className) attributes.push(`class="${htmlEncode(spec.className, true)}"`);
  if (spec.clientOnly) attributes.push("data-i18n-rich-client-only");
  if (spec.href) attributes.push(`href="${htmlEncode(spec.href, true)}"`);
  if (spec.external) attributes.push('target="_blank"', 'rel="noopener noreferrer"');
  if (spec.src) attributes.push(`src="${htmlEncode(spec.src, true)}"`);
  if (alt !== null) attributes.push(`alt="${htmlEncode(alt, true)}"`);
  if (spec.width) attributes.push(`width="${spec.width}"`);
  if (spec.height) attributes.push(`height="${spec.height}"`);
  if (spec.loading) attributes.push(`loading="${spec.loading}"`);
  if (spec.element === "i") attributes.push('aria-hidden="true"');
  return attributes.length > 0 ? ` ${attributes.join(" ")}` : "";
}

function serializeRichNodes(
  nodes: readonly RichTextNode[],
  specs: Readonly<Record<string, RichTranslationTagSpec>>,
): string {
  return nodes
    .map(node => {
      if (node.type === "text") return htmlEncode(node.value, true);
      const spec = specs[node.name];
      if (!spec) throw new Error(`Missing rich translation tag specification: ${node.name}`);
      const alt = spec.content === "alt" ? textContent(node.children) : null;
      const attributes = serializeAttributes(spec, alt);
      if (spec.element === "img") return `<img${attributes} />`;
      const children = spec.content === "children" ? serializeRichNodes(node.children, specs) : "";
      return `<${spec.element}${attributes}>${children}</${spec.element}>`;
    })
    .join("");
}

/** Renders one semantic rich translation for SSR using the shared application policy. */
export function renderRichTranslationHtml(
  translator: Translator,
  key: string,
  site: SiteConfig,
): string {
  const specs = createRichTranslationTagSpecs(site, false);
  const nodes = translateRichText(translator, key, { allowedTags: Object.keys(specs) });
  return serializeRichNodes(nodes, specs);
}
